import {trans} from '@frontend/helpers'
import type {
    AdPropertyConfigComponentPropsForFilters,
    AdPropertyConfigForFilters,
} from '@frontend/modules/public/ads/AdsModuleTypes'
import clsx from 'clsx'
import {
    useEffect,
    useState,
} from 'react'
import {Button} from 'swayok-react-mdb-ui-kit/components/Button/Button'
import {Collapse} from 'swayok-react-mdb-ui-kit/components/Collapse/Collapse'
import {Checkbox} from 'swayok-react-mdb-ui-kit/components/Input/Checkbox'
import {Input} from 'swayok-react-mdb-ui-kit/components/Input/Input'
import {toggleValueInArray} from 'swayok-react-mdb-ui-kit/helpers/form/toggleValueInArray'
import {filterOptions} from 'swayok-react-mdb-ui-kit/helpers/options_list/filterOptions'
import type {
    FormSelectOption,
    FormSelectOptionsList,
    HtmlComponentProps,
} from 'swayok-react-mdb-ui-kit/types'
import {FilterLabel} from './FilterLabel'

// Свойства компонента, получаемые из API.
interface ApiProps extends AdPropertyConfigComponentPropsForFilters {
    // Нужно ли показывать выбранные опции вверху списка?
    showSelectedOptionsAtTop?: boolean
    // Распределить опции на 2 колонки?
    twoColumns?: boolean
    // Кол-во опций, которое нужно отобразить явно. Остальные будут скрыты в <Collapse>.
    visibleOptionsCount?: number
    // Минимальное количество опций для отображения поля поиска по опциям.
    minOptionsCountToEnableSearch?: number
    // Дополнительные CSS классы для каждой опции.
    optionClass?: string
}

interface Props {
    config: AdPropertyConfigForFilters<ApiProps>
    values: (string | number | null)[]
    // Кол-во опций, которое нужно отобразить явно. Остальные будут скрыты в <Collapse>.
    visibleOptionsCount?: number
    // Минимальное количество опций для активации поиска по опциям.
    minOptionsCountToEnableSearch?: number
    onChange: (name: string, values: (string | number | null)[]) => void
    disabled?: boolean
    noLabel?: boolean
    className?: string
}

// Фильтр выбора нескольких значений из списка опций.
export function CheckboxesFilter(props: Props) {

    const {
        config,
        values = [],
        visibleOptionsCount = 5,
        minOptionsCountToEnableSearch,
        disabled,
        noLabel,
        className = 'pb-4',
        onChange,
    } = props

    const {
        label,
        className: classNameFromConfig,
        options = [],
        showSelectedOptionsAtTop,
        minOptionsCountToEnableSearch: minOptionsCountToEnableSearchFromConfig,
        twoColumns,
        optionClass,
        visibleOptionsCount: visibleOptionsCountFromConfig,
    } = config.props

    // Состояние видимости всех опций.
    const [
        isAllOptionsVisible,
        setIsAllOptionsVisible,
    ] = useState<boolean>(false)

    // Поиск по ключевым словам в опциях.
    const [
        keywords,
        setKeywords,
    ] = useState<string>('')
    // Отфильтрованные опции.
    const [
        filteredOptions,
        setFilteredOptions,
    ] = useState<FormSelectOptionsList<string | number | null>>(options)

    // Обновление списка опций.
    useEffect(() => {
        setFilteredOptions(filterOptions(keywords, options))
    }, [options, keywords])

    if (options.length === 0) {
        // Нет опций - нечего показывать.
        return null
    }

    // Сколько опций нужно явно отобразить.
    const visibleOptionsCountNormalized: number = Math.max(
        (
            visibleOptionsCountFromConfig
            ?? visibleOptionsCount
        ),
        values.length
    )

    const visibleOptions = filteredOptions.filter(
        (option, index) => (showSelectedOptionsAtTop
            ? values.includes(option.value)
            : index < visibleOptionsCountNormalized)
    )
    const hiddenOptions = filteredOptions.filter(
        (option, index) => (showSelectedOptionsAtTop
            ? !values.includes(option.value)
            : index >= visibleOptionsCountNormalized)
    )
    // Добавляем недостающее кол-во опций в блок видимых опций.
    if (
        showSelectedOptionsAtTop
        && visibleOptions.length < visibleOptionsCountNormalized
        && hiddenOptions.length > 0
    ) {
        for (let i = visibleOptions.length; i <= visibleOptionsCountNormalized; i++) {
            const option = hiddenOptions.shift()
            if (!option) {
                break
            }
            visibleOptions.push(option)
        }
    }

    // Отрисовка одной опции.
    const renderOption = (option: FormSelectOption<string | number | null>) => (
        <Checkbox
            key={option.value}
            label={option.label}
            checked={values.includes(option.value)}
            labelClassName="text-gray"
            wrapperClassName={clsx('mb-2 mt-0', optionClass)}
            disabled={disabled}
            onChange={event => {
                onChange(
                    config.name,
                    toggleValueInArray(
                        values,
                        option.value,
                        event.currentTarget.checked
                    )
                )
            }}
        />
    )

    // Нужно ли отобразить поле поиска по опциям?
    const enableQuickSearch: boolean = (
        isAllOptionsVisible
        && (
            !!minOptionsCountToEnableSearch
            || !!minOptionsCountToEnableSearchFromConfig
        )
        && (options.length >= (minOptionsCountToEnableSearchFromConfig ?? minOptionsCountToEnableSearch ?? 65535))
    )
    // Нужно ли отобразить кнопку "Показать все" / "Скрыть"?
    const enableShowHideButton: boolean = (hiddenOptions.length > 0 || keywords.trim().length > 0)

    // Свойства контейнера опций.
    const visibleOptionsContainerProps: HtmlComponentProps<HTMLDivElement> = {}
    const hiddenOptionsContainerProps: HtmlComponentProps<HTMLDivElement> = {}
    if (twoColumns) {
        visibleOptionsContainerProps.className = 'd-grid grid-columns-2 col-gap-2'
        visibleOptionsContainerProps.style = {
            gridAutoFlow: 'column',
            gridTemplateRows: `repeat(${Math.ceil(visibleOptions.length / 2)}, 1fr)`,
        }

        hiddenOptionsContainerProps.className = visibleOptionsContainerProps.className
        hiddenOptionsContainerProps.style = {
            gridAutoFlow: 'column',
            gridTemplateRows: `repeat(${Math.ceil(hiddenOptions.length / 2)}, 1fr)`,
        }
    }

    return (
        <div
            className={clsx(
                className,
                classNameFromConfig
            )}
        >
            {!noLabel && (
                <FilterLabel
                    label={label}
                    showResetButton={values.length > 0}
                    disabled={disabled}
                    onReset={() => onChange(config.name, [])}
                />
            )}
            <Collapse show={enableQuickSearch}>
                <Input
                    type="search"
                    placeholder={trans().ads.list.filters.search_options}
                    active
                    value={keywords}
                    small
                    className="py-2"
                    wrapperClassName="mb-2"
                    disabled={disabled}
                    onChange={event => setKeywords(event.currentTarget.value)}
                />
            </Collapse>
            <div
                className={clsx(
                    'position-relative',
                    enableQuickSearch ? 'shadow-bottom' : null
                )}
            >
                <div
                    className="overflow-auto"
                    style={{
                        maxHeight: 300,
                        minHeight: enableShowHideButton ? 80 : 0,
                    }}
                >
                    <div {...visibleOptionsContainerProps}>
                        {visibleOptions.map(renderOption)}
                    </div>
                    {hiddenOptions.length > 0 && (
                        <Collapse show={isAllOptionsVisible}>
                            <div {...hiddenOptionsContainerProps}>
                                {hiddenOptions.map(renderOption)}
                            </div>
                        </Collapse>
                    )}
                    {enableQuickSearch && keywords.trim().length > 0 && filteredOptions.length === 0 && (
                        <div className="text-center text-muted py-3">
                            {trans().ads.list.filters.no_options_found}
                        </div>
                    )}
                </div>
            </div>
            {enableShowHideButton && (
                <Button
                    color="link"
                    onClick={() => {
                        setIsAllOptionsVisible(visible => !visible)
                        setKeywords('')
                    }}
                    className="link-blue"
                >
                    {
                        isAllOptionsVisible
                            ? trans().ads.list.filters.hide_options
                            : trans().ads.list.filters.show_all_options
                    }
                </Button>
            )}
        </div>
    )
}
